import {
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from "@angular/core";
import { NgIf } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { ActivatedRoute, Router, RouterOutlet } from "@angular/router";
import { FuseLoadingBarComponent } from "@fuse/components/loading-bar";
import {
  FuseNavigationService,
  FuseVerticalNavigationComponent,
} from "@fuse/components/navigation";
import { FuseMediaWatcherService } from "@fuse/services/media-watcher";
import { NavigationService } from "app/core/navigation/navigation.service";
import { Navigation } from "app/core/navigation/navigation.types";
import { LanguagesComponent } from "app/layout/common/languages/languages.component";
import { NotificationsComponent } from "app/layout/common/notifications/notifications.component";
import { SupportComponent } from "app/layout/common/support/support.component";
import { UserComponent } from "app/layout/common/user/user.component";
import { VenueService } from "app/shared/services/venue.service";
import { environment } from "../../../../../environments/environment";

import {
  catchError,
  combineLatest,
  map,
  merge,
  of,
  shareReplay,
  startWith,
  Subject,
} from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
  selector: "classic-layout",
  templateUrl: "./classic.component.html",
  encapsulation: ViewEncapsulation.None,
  imports: [
    FuseLoadingBarComponent,
    FuseVerticalNavigationComponent,
    MatButtonModule,
    MatIconModule,
    LanguagesComponent,
    NotificationsComponent,
    SupportComponent,
    UserComponent,
    RouterOutlet,
    NgIf,
  ],
})
export class ClassicLayoutComponent implements OnInit, OnDestroy {
  isScreenSmall: boolean;
  navigation: Navigation = null;
  readonly appVersion = environment.appVersion;
  private _unsubscribeAll: Subject<any> = new Subject<any>();

  /**
   * Constructor
   */
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _router: Router,
    private destroyRef: DestroyRef,
    private _navigationService: NavigationService,
    private _fuseMediaWatcherService: FuseMediaWatcherService,
    private _fuseNavigationService: FuseNavigationService,
    private venueService: VenueService,
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Getter for current year
   */
  get currentYear(): number {
    return new Date().getFullYear();
  }
  userDetails: any = {};

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    try {
      this.userDetails =
        JSON.parse(localStorage.getItem("user") ?? "null") ?? {};
    } catch {
      // Corrupt browser state must not prevent the application shell
      // (including the navigation toggle) from being initialized.
      localStorage.removeItem("user");
      this.userDetails = {};
    }

    const venues$ = merge(
      this.venueService.getVenues().pipe(
        // Venue access is optional navigation enrichment. A failed
        // request must not prevent the base navigation from rendering.
        catchError(() => of([])),
      ),
      this.venueService.updated$,
    ).pipe(
      startWith([]),
      map((venues: unknown) => (Array.isArray(venues) ? venues : [])),
      map((venues) =>
        venues.map((venue) => ({
          id: venue._id,
          title: venue.name,
          type: "basic",
          icon: "mat_outline:location_on",
          link: `/venue/${venue._id}`,
        })),
      ),
      shareReplay(1),
    );

    // 2) Combine base navigation with venue items
    combineLatest([this._navigationService.navigation$, venues$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([navigation, venueItems]) => {
        const nav =
          typeof structuredClone === "function"
            ? structuredClone(navigation)
            : JSON.parse(JSON.stringify(navigation));

        // Work on the "default" layout group (index 0)
        const group = { ...nav.default?.[0] };
        const children = Array.isArray(group.children)
          ? [...group.children]
          : [];

        if (this.userDetails?.role === "staff") {
          // Keep only the "bookings" item
          const bookings = children.find((c) => c?.id === "bookings") ?? {
            id: "bookings",
            title: "Bookings",
            type: "basic",
            icon: "calendar_today",
            link: "/bookings",
          };

          group.children = [bookings];
        } else {
          // Non-staff: update the 'venues' node's children with venueItems
          const venuesIdx = children.findIndex((c) => c?.id === "venues");
          if (venuesIdx > -1) {
            const venuesNode = { ...children[venuesIdx], children: venueItems };
            children[venuesIdx] = venuesNode;
          }
          group.children = children;
        }

        // Write back immutably
        nav.default = [...(nav.default ?? [])];
        nav.default[0] = group;

        this.navigation = nav; // new reference => re-render
      });

    // 3) Media watcher (keep your existing logic)
    this._fuseMediaWatcherService.onMediaChange$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ matchingAliases }) => {
        this.isScreenSmall = !matchingAliases.includes("md");
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Toggle navigation
   *
   * @param name
   */
  toggleNavigation(name: string): void {
    // Get the navigation
    const navigation =
      this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
        name,
      );

    if (navigation) {
      // Toggle the opened status
      navigation.toggle();
    }
  }
}
